import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faStar, faMapMarkerAlt, faClock, faPlay, faPause } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Reveal from '../components/Reveal';
import Counter from '../components/Counter';
import { useLanguage } from '../context/LanguageContext';
import { MAPS_URL, MAPS_EMBED_URL, WHATSAPP_URL, GOOGLE_RATING } from '../siteInfo';
import './Home.css';

// Photo du haut de page : remplacer ce fichier pour changer l'ambiance de l'accueil
const HERO_IMAGE = '/img/atelier/atelier-interieur-large.webp';
// Film de l'atelier (montage Flow + voix off + beat) et sa boucle muette pour le fond
const HERO_LOOP = { webm: '/video/sdm_hero_loop.webm', mp4: '/video/sdm_hero_loop.mp4', poster: '/video/sdm_poster.jpg' };
const FILM = '/video/sdm_film.mp4';

// Pas de vidéo de fond si le visiteur a demandé moins d'animations ou économise ses données
const peutLireVideoFond = () => {
  if (typeof window === 'undefined') return false;
  const reduit = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const economie = navigator.connection && navigator.connection.saveData;
  return !reduit && !economie;
};

const Home = () => {
  const { t } = useLanguage();
  const [videoFond, setVideoFond] = useState(false);
  const [filmEnLecture, setFilmEnLecture] = useState(false);
  const filmRef = useRef(null);

  useEffect(() => { setVideoFond(peutLireVideoFond()); }, []);

  // Gros bouton lecture / pause au centre du lecteur
  const basculerFilm = () => {
    const v = filmRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      if (window.gtag) window.gtag('event', 'film_play', { source_page: 'home' });
    } else {
      v.pause();
    }
  };

  const services = [
    { key: 'service1', image: '/img/gallery3.webp' },
    { key: 'service2', image: '/img/gallery7.webp' },
    { key: 'service3', image: '/img/buffet-sculpte.webp' },
    { key: 'service4', image: '/img/gallery8.webp' }
  ];

  // Les images des étapes portent des noms fixes : on peut les remplacer
  // par les photos de l'atelier sans toucher au code.
  const steps = [1, 2, 3, 4].map((n) => ({
    n,
    title: t(`proc${n}_title`),
    text: t(`proc${n}_desc`),
    image: `/img/savoir-faire/etape-${n}.webp`
  }));

  const works = [
    { src: '/img/gallery7.webp', key: 'gal7' },
    { src: '/img/gallery5.webp', key: 'gal5' },
    { src: '/img/gallery13.webp', key: 'gal13' },
    { src: '/img/gallery8.webp', key: 'gal8' },
    { src: '/img/gallery9.webp', key: 'gal9' },
    { src: '/img/gallery3.webp', key: 'gal3' }
  ];

  return (
    <div className="home">
      {/* ===== Haut de page ===== */}
      <section className="home-hero" style={{ backgroundImage: `url(${HERO_IMAGE})` }}>
        {videoFond && (
          <video className="home-hero-video" autoPlay muted loop playsInline preload="metadata" poster={HERO_LOOP.poster} aria-hidden="true">
            <source src={HERO_LOOP.webm} type="video/webm" />
            <source src={HERO_LOOP.mp4} type="video/mp4" />
          </video>
        )}
        <div className="container home-hero-inner">
          <Reveal as="div" className="home-hero-text">
            <span className="eyebrow">{t('hero_badge')}</span>
            <h1>{t('hero_title')}</h1>
            <p>{t('hero_subtitle')}</p>
            <div className="home-hero-buttons">
              <Link to="/contact" className="btn btn-primary">{t('hero_cta_secondary')}</Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-light">
                <FontAwesomeIcon icon={faWhatsapp} /> {t('hero_whatsapp')}
              </a>
            </div>
          </Reveal>
        </div>
        <Link to="/gallery" className="home-hero-scroll">
          {t('hero_cta_primary')} <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </section>

      {/* ===== Chiffres clés ===== */}
      <section className="home-stats" aria-label="Chiffres clés">
        <div className="container home-stats-grid">
          <div className="home-stat">
            <strong><Counter value="15+" /></strong>
            <span>{t('stat_years')}</span>
          </div>
          <div className="home-stat">
            <strong><Counter value="500+" /></strong>
            <span>{t('stat_projects')}</span>
          </div>
          <div className="home-stat">
            <strong><Counter value="100%" /></strong>
            <span>{t('stat_clients_satisfied')}</span>
          </div>
          <div className="home-stat">
            <strong>{GOOGLE_RATING} <FontAwesomeIcon icon={faStar} className="home-stat-star" /></strong>
            <span>{t('stat_google')}</span>
          </div>
        </div>
      </section>

      {/* ===== Métiers ===== */}
      <section className="section">
        <div className="container">
          <Reveal as="div" className="section-head">
            <span className="eyebrow">{t('home_services_eyebrow')}</span>
            <h2>{t('home_preview_title')}</h2>
            <p>{t('home_services_sub')}</p>
          </Reveal>
          <div className="home-services">
            {services.map((service, index) => (
              <Reveal as={Link} to="/services" key={service.key} delay={index * 90} className="home-service">
                <div className="img-frame home-service-img">
                  <img src={service.image} alt="" loading="lazy" />
                </div>
                <h3>{t(`${service.key}_title`)}</h3>
                <p>{t(`${service.key}_desc`)}</p>
                <span className="home-service-more" aria-hidden="true">
                  <FontAwesomeIcon icon={faArrowRight} />
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Bois laminé : l'offre pour les professionnels et les budgets maîtrisés ===== */}
      <section className="section section-alt home-lamine">
        <div className="container home-lamine-grid">
          <Reveal as="div" animation="left" className="home-lamine-text">
            <span className="eyebrow">{t('home_lamine_eyebrow')}</span>
            <h2>{t('home_lamine_title')}</h2>
            <p>{t('home_lamine_desc')}</p>
            <ul className="home-features">
              <li><FontAwesomeIcon icon={faCheck} /> {t('home_lamine_p1')}</li>
              <li><FontAwesomeIcon icon={faCheck} /> {t('home_lamine_p2')}</li>
              <li><FontAwesomeIcon icon={faCheck} /> {t('home_lamine_p3')}</li>
              <li><FontAwesomeIcon icon={faCheck} /> {t('home_lamine_p4')}</li>
            </ul>
            <div className="home-lamine-buttons">
              <Link to="/contact?service=Mobilier%20en%20panneaux%20lamin%C3%A9s" className="btn btn-primary">{t('nav_quote_btn')}</Link>
              <Link to="/gallery" className="text-link">{t('home_lamine_gallery')} <FontAwesomeIcon icon={faArrowRight} /></Link>
            </div>
          </Reveal>
          <Reveal as="div" animation="right" delay={120} className="home-lamine-mosaic">
            {[
              ['lamine-05-chambre-hotel', 'gal21_alt'],
              ['lamine-04-bureaux', 'gal20_alt'],
              ['lamine-07-accueil', 'gal23_alt'],
              ['lamine-10-nuancier', 'home_lamine_nuancier_alt']
            ].map(([f, alt]) => (
              <div className="img-frame" key={f}>
                <img src={`/img/lamine/${f}.webp`} alt={t(alt)} loading="lazy" />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ===== Savoir-faire en 4 étapes ===== */}
      <section className="section section-dark">
        <div className="container">
          <Reveal as="div" className="section-head">
            <span className="eyebrow">{t('home_process_eyebrow')}</span>
            <h2>{t('home_process_title')}</h2>
          </Reveal>
          <ol className="home-steps">
            {steps.map((step, index) => (
              <Reveal as="li" key={step.n} delay={index * 110} className="home-step">
                <div className="img-frame home-step-img">
                  <img src={step.image} alt="" loading="lazy" />
                  <span className="home-step-num">0{step.n}</span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ===== Le film de l'atelier ===== */}
      <section className="section home-film">
        <div className="container">
          <Reveal as="div" className="section-head center">
            <span className="eyebrow">{t('home_film_eyebrow')}</span>
            <h2>{t('home_film_title')}</h2>
            <p>{t('home_film_sub')}</p>
          </Reveal>
          <Reveal as="div" className={`film-player ${filmEnLecture ? 'is-playing' : ''}`}>
            <video
              ref={filmRef}
              src={FILM}
              poster={HERO_LOOP.poster}
              controls
              playsInline
              preload="none"
              onPlay={() => setFilmEnLecture(true)}
              onPause={() => setFilmEnLecture(false)}
              onEnded={() => setFilmEnLecture(false)}
            />
            <button
              type="button"
              className="film-toggle"
              onClick={basculerFilm}
              aria-label={filmEnLecture ? t('film_pause') : t('film_play')}
            >
              <FontAwesomeIcon icon={filmEnLecture ? faPause : faPlay} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* ===== Réalisations ===== */}
      <section className="section">
        <div className="container">
          <div className="home-works-head">
            <Reveal as="div" className="section-head">
              <span className="eyebrow">{t('home_works_eyebrow')}</span>
              <h2>{t('home_works_title')}</h2>
            </Reveal>
            <Link to="/gallery" className="text-link">
              {t('home_works_link')} <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
          <div className="home-works">
            {works.map((work, index) => (
              <Reveal as={Link} to="/gallery" key={work.key} delay={(index % 3) * 90} className={`img-frame home-work home-work-${index + 1}`}>
                <img src={work.src} alt={t(`${work.key}_alt`)} loading="lazy" />
                <span className="home-work-title">{t(`${work.key}_title`)}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== L'atelier ===== */}
      <section className="section section-alt">
        <div className="container home-workshop">
          <Reveal as="div" animation="left" className="home-workshop-text">
            <span className="eyebrow">{t('home_workshop_eyebrow')}</span>
            <h2>{t('home_about_title')}</h2>
            <p>{t('home_about_desc')}</p>
            <ul className="home-features">
              <li><FontAwesomeIcon icon={faCheck} /> {t('home_feature1')}</li>
              <li><FontAwesomeIcon icon={faCheck} /> {t('home_feature2')}</li>
              <li><FontAwesomeIcon icon={faCheck} /> {t('home_feature3')}</li>
            </ul>
            <Link to="/about" className="btn btn-secondary">{t('nav_about')}</Link>
          </Reveal>
          <Reveal as="figure" animation="right" delay={120} className="img-frame home-workshop-img">
            <img src="/img/atelier/atelier-route-mbour.webp" alt={t('home_workshop_img_alt')} loading="lazy" />
          </Reveal>
        </div>

        <div className="container">
          <Reveal as="div" className="home-visit">
            <div className="home-visit-info">
              <h3>{t('home_visit_title')}</h3>
              <p><FontAwesomeIcon icon={faMapMarkerAlt} /> KM1 Route de Joal, Mbour, Sénégal</p>
              <p><FontAwesomeIcon icon={faClock} /> {t('contact_hours_weekday')}<br />{t('contact_hours_saturday')}</p>
              <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                {t('home_directions')}
              </a>
            </div>
            <div className="home-visit-map">
              <iframe
                title={t('contact_map_iframe_title')}
                src={MAPS_EMBED_URL}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== Avis ===== */}
      <section className="section">
        <div className="container home-review">
          <Reveal as="div" className="home-rating">
            <strong>{GOOGLE_RATING}</strong>
            <div className="home-rating-stars" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => <FontAwesomeIcon key={i} icon={faStar} />)}
              <FontAwesomeIcon icon={faStar} className="partial" />
            </div>
            <span>{t('home_reviews_rating_label')}</span>
          </Reveal>
          <Reveal as="figure" delay={120} className="home-quote">
            <span className="eyebrow">{t('home_reviews_eyebrow')}</span>
            <blockquote>« {t('home_review_quote')} »</blockquote>
            <figcaption>{t('home_review_source')}</figcaption>
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="text-link">
              {t('home_reviews_link')} <FontAwesomeIcon icon={faArrowRight} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* ===== Appel au devis ===== */}
      <section className="section section-dark cta-band">
        <Reveal as="div" className="container">
          <h2>{t('home_cta_title')}</h2>
          <p>{t('home_cta_desc')}</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">{t('nav_quote_btn')}</Link>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-light">
              <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
